/**
 * features/students/StudentProfile.tsx
 * ------------------------------------------------------------------
 * Student Profile v2 — Step 3 of Phase 1.
 *
 * Three-tab academic dashboard:
 *   Tab 1 — Academic: quick-stats cards, transcript grid, reports
 *   Tab 2 — Details: admissions data, guardians, medical, contacts
 *   Tab 3 — Discipline: incident records (read-only here)
 *
 * Reached via #/studentProfile/<studentId>.
 * Permission: canViewStudentProfile (admin, headteacher, class teacher).
 * ------------------------------------------------------------------
 */
import { useEffect, useMemo, useState } from 'react';
// Round 2: parent unlink
// Round 1: parent UX (issues 1, 2, 4)
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import {
  ArrowLeft,
  Download,
  Mail,
  MessageCircle,
  Phone,
  ShieldAlert,
  ExternalLink,
  FileText,
  GraduationCap,
  IdCard,
  User,
  Users,
  Stethoscope,
  ClipboardList,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Wallet,
  ArrowUpCircle,
  Calendar,
  Globe,
  BookOpen,
  MapPin,
  Shield,
  Briefcase,
  Building2,
  Heart,
  Star,
  // Round 2: parent unlink
  Link2Off,
  Loader2,
  X as XIcon,
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { db, functions, updateDoc } from '@/lib/firebase';
import { auditFromContext } from '@/lib/audit';
import { httpsCallable } from 'firebase/functions';
import { getPermissions } from '@/lib/permissions';
import { useAuth } from '@/contexts/AuthContext';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { canViewStudentProfile } from '@/lib/permissions';
import {
  buildTranscript,
  type TranscriptGrid,
} from '@/lib/transcript';
import { deriveMark, getFormLevel, getPassMark } from '@/lib/grading';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { SCHOOL } from '@/lib/school.config';
import { getPrimaryContact, getGuardianRoleLabel } from '@/lib/admissions';
import DocumentsTab from '@/features/documents/DocumentsTab';
import StudentHistoryTab from '@/features/students/StudentHistoryTab';
import StudentSubjectsTab from '@/features/students/StudentSubjectsTab';
import StudentFeesPanel from '@/features/finance/StudentFeesPanel';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { Card, PageHeader, Spinner } from '@/components/ui/primitives';
import StudentAvatar from '@/components/ui/StudentAvatar';
import type {
  Student,
  MarksDocument,
  GeneratedReport,
  DisciplineRecord,
} from '@/types';

interface Props {
  studentId: string;
  onBack: () => void;
}

type ProfileTab = 'academic' | 'subjects' | 'details' | 'discipline' | 'fees' | 'documents' | 'history';

const TABS: { id: ProfileTab; label: string; icon: typeof User }[] = [
  { id: 'academic', label: 'Academic', icon: GraduationCap },
  { id: 'subjects', label: 'Subjects', icon: BookOpen },
  { id: 'details', label: 'Details', icon: User },
  { id: 'discipline', label: 'Discipline', icon: ShieldAlert },
  { id: 'fees', label: 'Fees', icon: Wallet },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'history', label: 'History', icon: Clock },
];

export default function StudentProfile({ studentId, onBack }: Props) {
  const { userData } = useAuth();
  const { students, subjects, classes, teachers, users } = useData();

  const student: Student | null = useMemo(
    () => students.find((s) => s.id === studentId) ?? null,
    [students, studentId],
  );
  const classObj = classes.find((c) => c.id === student?.currentClass);
  const allowed = canViewStudentProfile(userData, student, teachers);
  const canManage = useMemo(
    () => getPermissions(userData?.roles ?? userData?.role ?? null).canManageStudents,
    [userData],
  );
  const [statusBusy, setStatusBusy] = useState(false);
  async function setStudentLeft(left: boolean) {
    if (!student) return;
    // Enrolment guard: real gender (M/F) is required before a student can be
    // set 'active' — blocks enrolling a 'pending' pupil while gender is still
    // the '' sentinel. (Inert for existing pupils, who already carry M/F.)
    if (!left && student.gender !== 'M' && student.gender !== 'F') {
      setErr('Set a valid gender (Male/Female) before enrolling this student.');
      return;
    }
    setStatusBusy(true);
    try {
      const now = new Date().toISOString();
      await updateDoc(
        doc(db, COLLECTIONS.students, student.id),
        left
          ? { status: 'left', exitReason: 'withdrawn', exitDate: now, lastClassId: student.currentClass, updatedAt: now, updatedBy: userData?.uid ?? '' }
          : { status: 'active', exitReason: null, exitDate: null, updatedAt: now, updatedBy: userData?.uid ?? '' },
      );
      auditFromContext(userData, left ? 'student_withdrawn' : 'student_enrolled', 'admissions', {
        targetId: student.id, targetType: 'student', targetName: student.fullName,
        details: { status: left ? 'left' : 'active', exitReason: left ? 'withdrawn' : null },
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setStatusBusy(false);
    }
  }

  const [tab, setTab] = useState<ProfileTab>('academic');
  const [marksDocs, setMarksDocs] = useState<MarksDocument[]>([]);
  const [reports, setReports] = useState<GeneratedReport[]>([]);
  const [disciplineRecords, setDisciplineRecords] = useState<DisciplineRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Round 1: parent UX (issues 1, 2, 4) — issue 4 portal status
  // Cache pending parent invitations for this student so the
  // 'Portal: invited' state can be detected without a user doc.
  const [pendingInvitations, setPendingInvitations] = useState<
    Array<{ guardianId: string; studentIds: string[] }>
  >([]);

  // Fetch marks, reports, and discipline records
  useEffect(() => {
    if (!allowed || !student) return;
    let cancel = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const [mSnap, rSnap, dSnap] = await Promise.all([
          getDocs(collection(db, COLLECTIONS.marks)),
          getDocs(
            query(
              collection(db, COLLECTIONS.generatedReports),
              where('studentId', '==', student.id),
            ),
          ),
          getDocs(
            query(
              collection(db, COLLECTIONS.discipline),
              where('studentId', '==', student.id),
              orderBy('createdAt', 'desc'),
            ),
          ),
        ]);
        if (cancel) return;
        const md = mSnap.docs
          .map((d) => d.data() as MarksDocument)
          .filter(
            (doc) =>
              doc.entries &&
              (doc.entries as Record<string, unknown>)[student.id] !==
                undefined,
          );
        const rd = rSnap.docs.map((d) => d.data() as GeneratedReport);
        rd.sort(
          (a, b) =>
            a.year.localeCompare(b.year) || a.term.localeCompare(b.term),
        );
        const dd = dSnap.docs.map((d) => ({ id: d.id, ...d.data() } as DisciplineRecord));
        setMarksDocs(md);
        setReports(rd);
        setDisciplineRecords(dd);
      } catch (e) {
        if (!cancel) setErr((e as Error).message);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [allowed, student]);

  // Round 1: parent UX (issues 1, 2, 4) — load pending parent invites
  // Permission to read parentInvitations is admin/headteacher only,
  // which matches canViewStudentProfile for non-teacher access.
  // Failures are swallowed because the badge is best-effort.
  useEffect(() => {
    if (!allowed || !student) return;
    let cancel = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, COLLECTIONS.parentInvitations),
            where('status', '==', 'pending'),
          ),
        );
        if (cancel) return;
        const rows = snap.docs
          .map((d) => d.data() as { guardianId: string; studentIds?: string[] })
          .filter((r) => (r.studentIds ?? []).includes(student.id))
          .map((r) => ({
            guardianId: r.guardianId,
            studentIds: r.studentIds ?? [],
          }));
        setPendingInvitations(rows);
      } catch {
        if (!cancel) setPendingInvitations([]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [allowed, student]);

  // Per-guardian parent portal status. Three states:
  //   active  — a parent user doc exists whose parentAccount
  //             .guardianId matches AND studentIds contains this
  //             student. (Authoritative.)
  //   invited — no user doc, but a pending invitation matches.
  //   none    — neither.
  type PortalStatus = 'active' | 'invited' | 'none';
  function getPortalStatus(guardianId: string | undefined): PortalStatus {
    if (!guardianId || !student) return 'none';
    const active = users.some(
      (u) =>
        u.role === 'parent' &&
        u.parentAccount?.guardianId === guardianId &&
        (u.parentAccount?.studentIds ?? []).includes(student.id),
    );
    if (active) return 'active';
    const invited = pendingInvitations.some((p) => p.guardianId === guardianId);
    return invited ? 'invited' : 'none';
  }

  // Round 2: parent unlink — unlink state + handler
  // The confirm-modal target is the guardianId we're about to
  // unlink. We resolve the parent user from useData.users.
  const [unlinkTarget, setUnlinkTarget] = useState<
    { guardianId: string; guardianName: string } | null
  >(null);
  const [unlinking, setUnlinking] = useState(false);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const canUnlink = getPermissions(userData?.roles ?? userData?.role).canManageUsers;

  function closeUnlinkModal() {
    if (unlinking) return;
    setUnlinkTarget(null);
    setUnlinkError(null);
  }

  async function performUnlink() {
    if (!unlinkTarget || !student) return;
    const parent = users.find(
      (u) =>
        u.role === 'parent' &&
        u.parentAccount?.guardianId === unlinkTarget.guardianId &&
        (u.parentAccount?.studentIds ?? []).includes(student.id),
    );
    if (!parent) {
      setUnlinkError('Could not resolve a parent account for this guardian.');
      return;
    }
    setUnlinking(true);
    setUnlinkError(null);
    try {
      const fn = httpsCallable(functions, 'removeChildLink');
      await fn({ parentUid: parent.uid, studentId: student.id });
      setUnlinkTarget(null);
      setUnlinkError(null);
      // DataContext owns the users subscription, so the badge will
      // flip from active to not-set-up on its own when the user
      // doc lands. No local refresh needed.
    } catch (e) {
      setUnlinkError((e as Error).message);
    } finally {
      setUnlinking(false);
    }
  }

  const transcript: TranscriptGrid = useMemo(
    () =>
      student
        ? buildTranscript({ studentId: student.id, marksDocs, subjects })
        : { subjects: [], terms: [], overallAverage: null, latestBySubject: {}, latestTerm: null },
    [student, marksDocs, subjects],
  );

  /* ---- Quick stats computed from transcript ---- */
  const quickStats = useMemo(() => {
    if (!student || transcript.terms.length === 0) return null;
    const latestTerm = transcript.terms[transcript.terms.length - 1];
    const prevTerm = transcript.terms.length >= 2 ? transcript.terms[transcript.terms.length - 2] : null;

    const currentAvg = latestTerm.termAverage;
    const prevAvg = prevTerm?.termAverage ?? null;
    const trend = currentAvg !== null && prevAvg !== null ? currentAvg - prevAvg : null;

    // Count passed/failed subjects for latest term
    const formLevel = getFormLevel(classObj?.name);
    const passMark = getPassMark(formLevel);
    let passed = 0;
    let failed = 0;
    for (const subj of transcript.subjects) {
      const cell = latestTerm.cells[subj.id];
      if (!cell || cell.mark === null) continue;
      if (cell.mark >= passMark) passed++;
      else failed++;
    }

    // Class rank: compare this student's latest average against all classmates
    // We can compute this from marksDocs for the latest term
    let rank: number | null = null;
    let classSize = 0;
    if (currentAvg !== null && classObj) {
      const classStudents = students.filter((s) => s.currentClass === classObj.id);
      classSize = classStudents.length;
      const latestKey = `${latestTerm.year}_${latestTerm.term}`;
      const latestMarkDoc = marksDocs.find(
        (m) => `${m.year}_${m.term}` === latestKey,
      );
      if (latestMarkDoc?.entries) {
        const averages: number[] = [];
        for (const cs of classStudents) {
          const entry = (latestMarkDoc.entries as any)[cs.id];
          if (!entry) continue;
          const marks: number[] = [];
          for (const sid of cs.subjects ?? []) {
            const sub = entry[sid];
            if (!sub) continue;
            // Numeric-only: uses d.termMark (scheme-independent) for the
            // class-rank average, so no gradeScheme is required here.
            const d = deriveMark(latestTerm.term, sub.ca, sub.exam);
            if (d.termMark !== null) marks.push(d.termMark);
          }
          if (marks.length > 0) {
            averages.push(Math.round(marks.reduce((a, b) => a + b, 0) / marks.length));
          }
        }
        averages.sort((a, b) => b - a);
        const idx = averages.indexOf(currentAvg);
        if (idx >= 0) rank = idx + 1;
      }
    }

    // Attendance % — currently term totals on student record
    const att = student as any;
    const daysPresent = typeof att.daysPresent === 'number' ? att.daysPresent : null;
    const daysAbsent = typeof att.daysAbsent === 'number' ? att.daysAbsent : null;
    let attendancePct: number | null = null;
    if (daysPresent !== null && daysAbsent !== null) {
      const total = daysPresent + daysAbsent;
      attendancePct = total > 0 ? Math.round((daysPresent / total) * 100) : null;
    }

    // Risk: simple flag based on failed subjects or low average
    const isAtRisk = failed > 0 || (currentAvg !== null && currentAvg < passMark + 5);

    return { currentAvg, prevAvg, trend, passed, failed, rank, classSize, attendancePct, isAtRisk };
  }, [student, transcript, marksDocs, students, classObj, subjects]);

  // ===== guards =====
  if (!student) {
    return (
      <div>
        <BackButton onBack={onBack} />
        <Card className="p-8 text-center">
          <p className="text-gray-500">
            Student not found.{' '}
            <button onClick={onBack} className="text-primary hover:underline">Go back</button>.
          </p>
        </Card>
      </div>
    );
  }
  if (!allowed) {
    return (
      <div>
        <BackButton onBack={onBack} />
        <Card className="p-8 text-center">
          <ShieldAlert size={32} className="mx-auto text-amber-500 mb-3" />
          <p className="font-semibold mb-1">You don't have access to this student's profile.</p>
          <p className="text-sm text-gray-500">
            Student profiles are visible to admins, the headteacher, academic administrators, and this student's class teacher.
          </p>
        </Card>
      </div>
    );
  }

  // ===== transcript PDF (same as v1) =====
  const subjectName = (id: string) => subjects.find((s) => s.id === id)?.name ?? id;
  const teacherName = (id?: string) => teachers.find((t) => t.id === id)?.displayName ?? '—';

  async function downloadTranscript() {
    // header row
    const cols = ['Subject', ...transcript.terms.map((t) => t.label)];

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const W = pdf.internal.pageSize.getWidth();
    const M = 12;
    const colW = (W - 2 * M) / cols.length;
    let y = M;

    const logo = await loadLogoDataUrl();
    if (logo) {
      pdf.addImage(logo, 'PNG', M, y, 18, 18);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text(SCHOOL.name, M + 22, y + 8);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(`Academic Transcript — ${student!.fullName} (${classObj?.name ?? ''})`, M + 22, y + 14);
      y += 24;
    } else {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text(SCHOOL.name, M, y + 6);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.text(`Academic Transcript — ${student!.fullName} (${classObj?.name ?? ''})`, M, y + 12);
      y += 18;
    }

    // header
    pdf.setFillColor(240, 240, 240);
    pdf.rect(M, y, W - 2 * M, 8, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    cols.forEach((c, i) => pdf.text(c, M + i * colW + 2, y + 5.5));
    y += 10;

    // rows
    pdf.setFont('helvetica', 'normal');
    for (const subj of transcript.subjects) {
      if (y > 190) { pdf.addPage(); y = M; }
      pdf.text(subj.name, M + 2, y + 4);
      transcript.terms.forEach((t, i) => {
        const cell = t.cells[subj.id];
        const txt = cell
          ? cell.mark === null
            ? 'ABS'
            : `${cell.mark}%  ${cell.grade !== '-' ? cell.grade : ''}`
          : '—';
        pdf.text(txt, M + (i + 1) * colW + 2, y + 4);
      });
      y += 7;
    }

    // averages row
    y += 2;
    pdf.setFont('helvetica', 'bold');
    pdf.text('Term average', M + 2, y + 4);
    transcript.terms.forEach((t, i) => {
      pdf.text(t.termAverage === null ? '—' : `${t.termAverage}%`, M + (i + 1) * colW + 2, y + 4);
    });
    y += 10;
    if (transcript.overallAverage !== null) {
      pdf.text(`Overall average: ${transcript.overallAverage}%`, M, y + 4);
    }

    // footer
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(120, 120, 120);
    pdf.text(
      `Generated ${new Date().toLocaleString()} · This transcript aggregates report data; it is not a substitute for individual term reports.`,
      M,
      290,
    );
    pdf.save(`transcript-${student!.fullName.replace(/\s+/g, '_')}.pdf`);
  }

  // Group reports by year+term
  const reportsByTerm: Record<string, GeneratedReport[]> = {};
  for (const r of reports) {
    const k = `${r.year} · ${r.term.replace('Term', 'Term ')}`;
    (reportsByTerm[k] ?? (reportsByTerm[k] = [])).push(r);
  }

  const primaryContact = getPrimaryContact(student);
  // Round 3: header portal pill — find the primary guardian's id so
  // we can show their portal status at-a-glance in the header card.
  // Mirrors getPrimaryContact's selection: first guardian flagged
  // isPrimaryContact, else the first guardian.
  const primaryGuardianId = (() => {
    const gs = student.admissions?.guardians ?? [];
    if (gs.length === 0) return null;
    return (gs.find((g) => g.isPrimaryContact) ?? gs[0]).id ?? null;
  })();
  const primaryPortalStatus = primaryGuardianId
    ? getPortalStatus(primaryGuardianId)
    : 'none';

  // Documents tab is gated to admissions staff (admin/headteacher/admissions)
  const canSeeDocuments =
    userData?.role === 'admin' ||
    userData?.role === 'headteacher' ||
    userData?.role === 'admissions';
  const visibleTabs = TABS.filter((t) => t.id !== 'documents' || canSeeDocuments);

  return (
    <div>
      <BackButton onBack={onBack} />
      <PageHeader
        title="Student Profile"
        subtitle={student.fullName}
        actions={
          <div className="flex items-center gap-2">
            {transcript.terms.length > 0 && (
              <button
                onClick={downloadTranscript}
                className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl border border-primary/30 text-primary hover:bg-primary/5"
              >
                <Download size={14} /> Download transcript
              </button>
            )}
            {canManage && (
              student.status === 'left' ? (
                <button
                  onClick={() => setStudentLeft(false)}
                  disabled={statusBusy}
                  className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl border border-emerald-300 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-40"
                >
                  {statusBusy ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpCircle size={14} />} Reinstate
                </button>
              ) : (
                <button
                  onClick={() => setStudentLeft(true)}
                  disabled={statusBusy}
                  className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl border border-amber-300 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-40"
                >
                  {statusBusy ? <Loader2 size={14} className="animate-spin" /> : <Link2Off size={14} />} Mark as left
                </button>
              )
            )}
          </div>
        }
      />

      {/* Header card — student identity */}
      <Card className="p-5 mb-5">
        <div className="flex items-start gap-4">
          <StudentAvatar student={student} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-lg text-gray-800 dark:text-white">
              {student.fullName}
            </p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 mt-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                <GraduationCap size={13} />
                {classObj?.name ?? student.currentClass}
              </span>
              {student.status === 'left' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 font-medium">
                  Left{student.exitReason ? ` · ${student.exitReason}` : ''}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <IdCard size={13} />
                Adm: {student.admissionNumber ?? '—'}
              </span>
              <span>{student.gender === 'F' ? 'Female' : 'Male'}</span>
            </div>
            {primaryContact && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                <span className="text-gray-400 uppercase tracking-wide font-semibold text-[10px]">
                  Primary contact
                </span>
                {primaryContact.name && <span>{primaryContact.name}</span>}
                <span className="inline-flex items-center gap-1">
                  <Mail size={12} className={primaryContact.email ? 'text-emerald-600' : 'text-gray-300'} />
                  {primaryContact.email || 'no email'}
                </span>
                <span className="inline-flex items-center gap-1">
                  <MessageCircle size={12} className={primaryContact.whatsapp ? 'text-emerald-600' : 'text-gray-300'} />
                  {primaryContact.whatsapp || 'no whatsapp'}
                </span>
                {/* Round 3: header portal pill */}
                {primaryGuardianId && (
                  primaryPortalStatus === 'active' ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-xs font-semibold"
                      title="The primary contact has an active parent portal account."
                    >
                      <CheckCircle2 size={12} /> Parent Profile Active
                    </span>
                  ) : primaryPortalStatus === 'invited' ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700 text-xs font-semibold"
                      title="An invitation has been sent but the parent has not signed in yet."
                    >
                      <Clock size={12} /> Parent Profile Invited
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300 text-xs font-semibold"
                      title="The primary contact does not have a parent portal account."
                    >
                      No Parent Profile
                    </span>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Tab strip */}
      <Card className="p-1.5 mb-5">
        <div className="flex gap-1">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            const badge = t.id === 'discipline' && disciplineRecords.length > 0
              ? disciplineRecords.length
              : null;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all whitespace-nowrap ${
                  active
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
              >
                <Icon size={16} />
                {t.label}
                {badge !== null && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    active ? 'bg-white/20 text-white' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                  }`}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Loading / error */}
      {loading && (
        <Card className="p-6 text-center">
          <Spinner /> Loading…
        </Card>
      )}
      {err && (
        <Card className="p-4 mb-5 border-red-200 bg-red-50 dark:bg-red-900/20">
          <p className="text-sm text-red-700 dark:text-red-300">{err}</p>
        </Card>
      )}

      {/* Tab content */}
      {!loading && tab === 'academic' && renderAcademicTab()}
      {!loading && tab === 'subjects' && <StudentSubjectsTab student={student} />}
      {!loading && tab === 'details' && renderDetailsTab()}
      {!loading && tab === 'discipline' && renderDisciplineTab()}
      {!loading && tab === 'fees' && <StudentFeesTab student={student} />}
      {!loading && tab === 'documents' && canSeeDocuments && (
        <DocumentsTab
          studentId={student.id}
          uploader={{ uid: userData?.uid ?? '', displayName: userData?.displayName }}
        />
      )}
      {/* Round 10: per-student audit history */}
      {!loading && tab === 'history' && (
        <StudentHistoryTab student={student} />
      )}
      {/* Round 2: parent unlink — unlink confirm modal */}
      {unlinkTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={closeUnlinkModal}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white/95 dark:bg-gray-900/85 backdrop-blur-xl border border-white/50 dark:border-white/10 ring-1 ring-black/5 shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Unlink parent from this student?
              </h3>
              <button
                onClick={closeUnlinkModal}
                disabled={unlinking}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-40"
                aria-label="Close"
              >
                <XIcon size={18} />
              </button>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
              <strong>{unlinkTarget.guardianName}</strong> will lose access
              to <strong>{student?.fullName}</strong>&apos;s reports on all
              devices immediately. Other children linked to this parent are
              not affected.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              The parent account itself stays active and can be re-linked
              later by sending a fresh invitation.
            </p>
            {unlinkError && (
              <div className="mb-3 text-xs px-3 py-2 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800">
                {unlinkError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={closeUnlinkModal}
                disabled={unlinking}
                className="px-3 py-1.5 rounded-lg text-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/30 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={performUnlink}
                disabled={unlinking}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40"
              >
                {unlinking ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Link2Off size={14} />
                )}
                Unlink
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  /* ================================================================
     TAB 1 — ACADEMIC
     ================================================================ */

  function renderAcademicTab() {
    return (
      <div className="space-y-5">
        {/* Quick stats cards */}
        {quickStats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <QuickStat
              label="Current Average"
              value={quickStats.currentAvg !== null ? `${quickStats.currentAvg}%` : '—'}
              tone={quickStats.currentAvg !== null && quickStats.currentAvg >= 50 ? 'emerald' : 'red'}
            />
            <QuickStat
              label="Class Rank"
              value={quickStats.rank !== null ? `${quickStats.rank}/${quickStats.classSize}` : '—'}
              tone="blue"
            />
            <QuickStat
              label="Attendance"
              value={quickStats.attendancePct !== null ? `${quickStats.attendancePct}%` : '—'}
              tone={quickStats.attendancePct !== null && quickStats.attendancePct >= 80 ? 'emerald' : 'gold'}
            />
            <QuickStat
              label="Trend"
              value={quickStats.trend !== null
                ? `${quickStats.trend >= 0 ? '+' : ''}${quickStats.trend}%`
                : '—'}
              tone={quickStats.trend !== null ? (quickStats.trend >= 0 ? 'emerald' : 'red') : 'gray'}
              icon={quickStats.trend !== null
                ? quickStats.trend > 0 ? TrendingUp : quickStats.trend < 0 ? TrendingDown : Minus
                : undefined}
            />
            <QuickStat
              label="Subjects Passed"
              value={`${quickStats.passed}/${quickStats.passed + quickStats.failed}`}
              tone={quickStats.failed === 0 ? 'emerald' : 'gold'}
            />
            <QuickStat
              label="Risk"
              value={quickStats.isAtRisk ? 'At Risk' : 'None'}
              tone={quickStats.isAtRisk ? 'red' : 'emerald'}
              icon={quickStats.isAtRisk ? AlertTriangle : CheckCircle2}
            />
          </div>
        )}

        {/* Subjects + teachers */}
        <Card className="p-5">
          <h3 className="font-bold text-gray-800 dark:text-white mb-3">
            Subjects ({student!.subjects?.length ?? 0})
          </h3>
          {(!student!.subjects || student!.subjects.length === 0) ? (
            <p className="text-sm text-gray-500">No subjects recorded for this student.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {student!.subjects.map((sid) => (
                <div
                  key={sid}
                  className="flex items-center justify-between px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700"
                >
                  <span className="font-medium text-sm">{subjectName(sid)}</span>
                  <span className="text-xs text-gray-500 truncate ml-2">
                    {teacherName(student!.subjectTeachers?.[sid])}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Transcript grid */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-bold text-gray-800 dark:text-white">Academic transcript</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              One row per subject, one column per term with data.
              {transcript.terms.length === 1 &&
                ' Only one term of data so far — the grid will fill out as more terms are recorded.'}
              {transcript.terms.length === 0 &&
                ' No marks recorded for this student yet.'}
            </p>
          </div>
          {transcript.terms.length === 0 ? null : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500">
                  <tr>
                    <th className="px-3 py-2.5 font-medium">Subject</th>
                    {transcript.terms.map((t) => (
                      <th key={`${t.year}-${t.term}`} className="px-3 py-2.5 font-medium text-center">
                        {t.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {transcript.subjects.map((subj) => (
                    <tr key={subj.id}>
                      <td className="px-3 py-2 font-medium">{subj.name}</td>
                      {transcript.terms.map((t) => {
                        const cell = t.cells[subj.id];
                        return (
                          <td key={`${t.year}-${t.term}`} className="px-3 py-2 text-center">
                            {cell ? (
                              <span className="inline-flex items-center gap-1.5">
                                <span className="text-sm">
                                  {cell.mark === null ? '–' : `${cell.mark}%`}
                                </span>
                                {cell.grade !== '-' && (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700">
                                    {cell.grade}
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-300">–</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="bg-primary/5 font-semibold">
                    <td className="px-3 py-2">Term average</td>
                    {transcript.terms.map((t) => (
                      <td key={`${t.year}-${t.term}-avg`} className="px-3 py-2 text-center">
                        {t.termAverage === null ? '–' : `${t.termAverage}%`}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
              {transcript.overallAverage !== null && (
                <div className="px-4 py-3 bg-primary/10 text-primary font-semibold text-sm">
                  Overall average across {transcript.terms.length} term(s): {transcript.overallAverage}%
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Reports */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-bold text-gray-800 dark:text-white">
              Generated reports ({reports.length})
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Every report card generated for this student, across terms.
            </p>
          </div>
          {reports.length === 0 ? (
            <p className="text-sm text-gray-500 p-6 text-center">
              No reports have been generated for this student yet.
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {Object.entries(reportsByTerm).map(([termLabel, rs]) => (
                <div key={termLabel} className="p-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {termLabel}
                  </p>
                  {rs.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between gap-2 p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{r.className}</p>
                        <p className="text-xs text-gray-500">
                          Generated {new Date(r.generatedAt).toLocaleDateString()} · by {r.generatedByName}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {r.downloadUrl ? (
                          <>
                            <a
                              href={r.downloadUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                            >
                              <ExternalLink size={13} /> Open
                            </a>
                            <a
                              href={r.downloadUrl}
                              download={`${student!.fullName}-${r.term}-${r.year}.pdf`}
                              className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                            >
                              <Download size={13} />
                            </a>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">
                            <FileText size={12} className="inline" /> no PDF stored
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    );
  }

  /* ================================================================
     TAB 2 — DETAILS
     ================================================================ */

  function renderDetailsTab() {
    const a = student!.admissions;
    const guardians = a?.guardians ?? [];
    const medical = a?.medical;

    return (
      <div className="space-y-5">
        {!a && (
          <Card className="p-5 text-center">
            <p className="text-gray-500 text-sm">
              No admissions data has been entered for this student yet.
              Complete their intake in the Admissions module.
            </p>
          </Card>
        )}

        {/* Personal information */}
        {a && (
          <Card className="p-5">
            <h3 className="font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <User size={18} className="text-primary" /> Personal Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailRow icon={User} label="Full Name" value={`${a.firstName ?? ''} ${a.surname ?? ''}`.trim() || student!.fullName} />
              <DetailRow label="Gender" value={student!.gender === 'F' ? 'Female' : 'Male'} />
              <DetailRow icon={Calendar} label="Date of Birth" value={(student!.dateOfBirth ?? a.dateOfBirth) ? new Date((student!.dateOfBirth ?? a.dateOfBirth)!).toLocaleDateString() : '—'} />
              <DetailRow icon={IdCard} label="Admission Number" value={student!.admissionNumber ?? '—'} />
              <DetailRow icon={GraduationCap} label="Class" value={classObj?.name ?? student!.currentClass} />
              <DetailRow icon={Globe} label="Nationality" value={a.nationality ?? '—'} />
              <DetailRow icon={IdCard} label="National ID" value={a.nationalId ?? '—'} />
              <DetailRow icon={IdCard} label="Passport No." value={a.passportNumber ?? '—'} />
              <DetailRow label="Student Email" value={a.studentEmail ?? '—'} />
              <DetailRow icon={BookOpen} label="Home Language" value={a.homeLanguage ?? '—'} />
              <DetailRow label="Religion" value={a.religion ?? '—'} />
              <DetailRow label="Boarding Status" value={a.boardingStatus === 'boarding' ? 'Boarding' : 'Day Scholar'} />
              <DetailRow icon={MapPin} label="Address" value={a.address ?? '—'} full />
            </div>
          </Card>
        )}

        {/* Guardians */}
        {guardians.length > 0 && (
          <Card className="p-5">
            <h3 className="font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <Users size={18} className="text-primary" /> Guardians ({guardians.length})
            </h3>
            <div className="space-y-4">
              {guardians.map((g) => (
                <div
                  key={g.id}
                  className={`p-4 rounded-xl border ${
                    g.isPrimaryContact
                      ? 'border-amber-200 bg-amber-50/50 dark:border-amber-700 dark:bg-amber-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {g.fullName}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {getGuardianRoleLabel(g)}
                    </span>
                    {g.isPrimaryContact && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                        <Star size={10} className="fill-current" /> Primary
                      </span>
                    )}
                    {/* Round 1: parent UX (issues 1, 2, 4) — issue 4 */}
                    {(() => {
                      const status = getPortalStatus(g.id);
                      if (status === 'active') {
                        if (canUnlink) {
                          return (
                            <button
                              type="button"
                              onClick={() =>
                                setUnlinkTarget({
                                  guardianId: g.id,
                                  guardianName: g.fullName,
                                })
                              }
                              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/60 transition-colors"
                              title="Parent portal account is active. Click to unlink."
                            >
                              <CheckCircle2 size={10} /> Portal: active
                              <Link2Off size={10} className="opacity-60" />
                            </button>
                          );
                        }
                        return (
                          <span
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                            title="A parent portal account is active for this guardian."
                          >
                            <CheckCircle2 size={10} /> Portal: active
                          </span>
                        );
                      }
                      if (status === 'invited') {
                        return (
                          <span
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700"
                            title="An invitation has been sent but no account is active yet."
                          >
                            <Clock size={10} /> Portal: invited
                          </span>
                        );
                      }
                      return (
                        <span
                          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700/60 dark:text-gray-400"
                          title="No parent portal account exists for this guardian."
                        >
                          Portal: not set up
                        </span>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    {g.phone && (
                      <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Phone size={13} className="text-gray-400" /> {g.phone}
                      </span>
                    )}
                    {g.whatsapp && (
                      <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <MessageCircle size={13} className="text-emerald-500" /> {g.whatsapp}
                      </span>
                    )}
                    {g.email && (
                      <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Mail size={13} className="text-gray-400" /> {g.email}
                      </span>
                    )}
                    {g.occupation && (
                      <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Briefcase size={13} className="text-gray-400" /> {g.occupation}
                        {g.employer ? ` at ${g.employer}` : ''}
                      </span>
                    )}
                    {g.idNumber && (
                      <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                        <Shield size={13} className="text-gray-400" /> {g.idNumber}
                      </span>
                    )}
                    {g.address && (
                      <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-400 sm:col-span-2">
                        <MapPin size={13} className="text-gray-400" /> {g.address}
                      </span>
                    )}
                  </div>
                  {/* Consent flags */}
                  {(g.consentToSend || g.consentToWhatsapp) && (
                    <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      {g.consentToSend && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 size={12} /> Consent to email reports
                        </span>
                      )}
                      {g.consentToWhatsapp && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 size={12} /> Consent to WhatsApp
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Emergency contact */}
        {a && (a.emergencyContactName || a.emergencyContactPhone) && (
          <Card className="p-5">
            <h3 className="font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <AlertTriangle size={18} className="text-primary" /> Emergency Contact
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailRow icon={User} label="Name" value={a.emergencyContactName ?? '—'} />
              <DetailRow icon={Phone} label="Phone" value={a.emergencyContactPhone ?? '—'} />
            </div>
          </Card>
        )}

        {/* Medical */}
        {medical && (medical.doctorName || medical.doctorPhone || medical.medicalAidProvider || medical.medicalAidNumber || medical.allergies || medical.chronicConditions || medical.medications || medical.bloodType) && (
          <Card className="p-5">
            <h3 className="font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <Stethoscope size={18} className="text-primary" /> Medical Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailRow icon={Stethoscope} label="Doctor" value={medical.doctorName ?? '—'} />
              <DetailRow icon={Phone} label="Doctor Phone" value={medical.doctorPhone ?? '—'} />
              <DetailRow label="Medical Aid" value={medical.medicalAidProvider ? `${medical.medicalAidProvider} — ${medical.medicalAidNumber ?? ''}` : '—'} />
              <DetailRow label="Blood Type" value={medical.bloodType ?? '—'} />
              {medical.allergies && (
                <DetailRow icon={AlertTriangle} label="Allergies" value={medical.allergies} full />
              )}
              {medical.chronicConditions && (
                <DetailRow icon={Heart} label="Chronic Conditions" value={medical.chronicConditions} full />
              )}
              {medical.medications && (
                <DetailRow label="Medications" value={medical.medications} full />
              )}
            </div>
          </Card>
        )}

        {/* Administrative */}
        {a && (
          <Card className="p-5">
            <h3 className="font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <ClipboardList size={18} className="text-primary" /> Administrative
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DetailRow icon={Building2} label="Previous School" value={a.previousSchool ?? '—'} />
              <DetailRow icon={Calendar} label="Date of Admission" value={a.dateOfAdmission ? new Date(a.dateOfAdmission).toLocaleDateString() : '—'} />
              {a.notes && <DetailRow label="Notes" value={a.notes} full />}
            </div>
          </Card>
        )}
      </div>
    );
  }

  /* ================================================================
     TAB 3 — DISCIPLINE
     ================================================================ */

  function renderDisciplineTab() {
    const SEVERITY_COLORS: Record<string, string> = {
      low: 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      medium: 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
      high: 'bg-orange-50 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
      critical: 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    };

    const CATEGORY_LABELS: Record<string, string> = {
      lateness: 'Lateness',
      uniform: 'Uniform',
      bullying: 'Bullying',
      fighting: 'Fighting',
      academic_misconduct: 'Academic Misconduct',
      disrespect: 'Disrespect',
      absenteeism: 'Absenteeism',
      substance_abuse: 'Substance Abuse',
      vandalism: 'Vandalism',
      other: 'Other',
    };

    return (
      <div className="space-y-3">
        {disciplineRecords.length === 0 ? (
          <Card className="p-8 text-center">
            <CheckCircle2 size={32} className="mx-auto text-emerald-400 mb-3" />
            <p className="text-gray-500 text-sm">No discipline records for this student.</p>
          </Card>
        ) : (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <QuickStat
                label="Total"
                value={String(disciplineRecords.length)}
                tone="blue"
              />
              <QuickStat
                label="Open"
                value={String(disciplineRecords.filter((r) => r.status === 'open').length)}
                tone="gold"
              />
              <QuickStat
                label="High / Critical"
                value={String(disciplineRecords.filter((r) => r.severity === 'high' || r.severity === 'critical').length)}
                tone="red"
              />
            </div>

            {/* Records */}
            {disciplineRecords.map((r) => {
              const StatusIcon = r.status === 'resolved' ? CheckCircle2 : r.status === 'escalated' ? ArrowUpCircle : Clock;
              return (
                <Card key={r.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {r.incidentDate}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          {CATEGORY_LABELS[r.category] ?? r.category}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${SEVERITY_COLORS[r.severity] ?? ''}`}>
                          {r.severity.charAt(0).toUpperCase() + r.severity.slice(1)}
                        </span>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                          r.status === 'resolved'
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : r.status === 'escalated'
                            ? 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                            : 'bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                        }`}>
                          <StatusIcon size={12} />
                          {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{r.description}</p>
                      {r.actionTaken && (
                        <p className="text-xs text-gray-500 mt-1">
                          <span className="font-medium">Action:</span> {r.actionTaken}
                        </p>
                      )}
                      <p className="text-[10px] text-gray-400 mt-1.5">
                        Recorded by {r.recordedByName} · {new Date(r.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </>
        )}
      </div>
    );
  }
}

/* ==================================================================
   HELPER COMPONENTS
   ================================================================== */

function BackButton({ onBack }: { onBack: () => void }) {
  return (
    <button
      onClick={onBack}
      className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary mb-3"
    >
      <ArrowLeft size={14} /> Back
    </button>
  );
}

function QuickStat({
  label,
  value,
  tone,
  icon: Icon,
}: {
  label: string;
  value: string;
  tone: 'emerald' | 'blue' | 'gold' | 'red' | 'purple' | 'gray';
  icon?: typeof TrendingUp;
}) {
  const BG: Record<string, string> = {
    emerald: 'bg-emerald-50 border-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800',
    blue: 'bg-blue-50 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800',
    gold: 'bg-amber-50 border-amber-100 dark:bg-amber-900/20 dark:border-amber-800',
    red: 'bg-red-50 border-red-100 dark:bg-red-900/20 dark:border-red-800',
    purple: 'bg-purple-50 border-purple-100 dark:bg-purple-900/20 dark:border-purple-800',
    gray: 'bg-gray-50 border-gray-100 dark:bg-gray-800 dark:border-gray-700',
  };
  const TEXT: Record<string, string> = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    blue: 'text-blue-700 dark:text-blue-300',
    gold: 'text-amber-700 dark:text-amber-300',
    red: 'text-red-700 dark:text-red-300',
    purple: 'text-purple-700 dark:text-purple-300',
    gray: 'text-gray-700 dark:text-gray-300',
  };
  return (
    <div className={`rounded-xl border p-3 ${BG[tone]}`}>
      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-lg font-bold ${TEXT[tone]} flex items-center gap-1.5`}>
        {Icon && <Icon size={16} />}
        {value}
      </p>
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
  full,
}: {
  icon?: typeof User;
  label: string;
  value: string;
  full?: boolean;
}) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-0.5 flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </p>
      <p className="text-sm text-gray-800 dark:text-gray-200">{value}</p>
    </div>
  );
}

/* ------------------------------------------------------------------
 * Fees tab — loads this student's invoices + receipts (staff context)
 * and renders the shared StudentFeesPanel. Mounted only when the Fees
 * tab is active, so finance subscriptions don't run on every profile.
 * ---------------------------------------------------------------- */
function StudentFeesTab({ student }: { student: Student }) {
  const fin = useFinanceData();
  if (fin.loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
        <Spinner /> Loading fees…
      </div>
    );
  }
  const invoices = fin.invoices.filter((i) => i.studentId === student.id);
  const payments = fin.payments.filter((p) => p.studentId === student.id);
  return (
    <StudentFeesPanel
      studentName={student.fullName}
      studentId={student.id}
      invoices={invoices}
      payments={payments}
      canResend
      recipientEmail={getPrimaryContact(student)?.email}
    />
  );
}
